import { useState, useEffect, useMemo } from 'react';
import {
  MagnifyingGlassIcon,
  TrophyIcon,
  PencilIcon,
  PlusIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { supabase } from '../../../lib/supabase';
import { useTournament } from '../../TournamentContext';
import MobilePlayerScoreSheet from '../../components/MobilePlayerScoreSheet';
import MobilePlayerSelectionSheet from '../../components/MobilePlayerSelectionSheet';
import { toast } from 'react-hot-toast';

interface Score {
  id: string;
  player_id: string;
  tournament_id: string;
  round: number;
  score?: number | null;
  date: string;
  notes?: string;
  category?: string;
  player_status?: 'PLAYED' | 'DNP' | 'WD' | 'DQ';
  player?: {
    first_name: string;
    last_name: string;
    country: string;
    resident_club: string;
    category: string;
  };
}

interface Player {
  id: string;
  first_name: string;
  last_name: string;
  category: string;
  country: string;
  resident_club?: string;
}

export default function MobileAdminScores() {
  const { selectedTournament } = useTournament();
  const tournamentId = selectedTournament?.id || '';
  const [scores, setScores] = useState<Score[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roundFilter, setRoundFilter] = useState('All');
  const [editingScore, setEditingScore] = useState<Score | null>(null);
  const [isPlayerSelectOpen, setIsPlayerSelectOpen] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);

  useEffect(() => { if (tournamentId) { fetchScores(); fetchPlayers(); } }, [tournamentId]);

  const fetchScores = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('player_scores')
        .select('*, player:players(first_name, last_name, country, resident_club, category)')
        .eq('tournament_id', tournamentId)
        .order('round', { ascending: true });
      if (error) throw error;
      setScores(data || []);
    } catch {
      toast.error('Failed to load scores');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayers = async () => {
    try {
      const { data } = await supabase
        .from('players')
        .select('id, first_name, last_name, category, country, resident_club')
        .eq('tournament_id', tournamentId)
        .eq('status', 'verified');
      setPlayers(data || []);
    } catch {}
  };

  const playersWithScores = useMemo(() => {
    const map: Record<string, number[]> = {};
    scores.forEach(s => {
      if (!map[s.player_id]) map[s.player_id] = [];
      map[s.player_id].push(s.round);
    });
    return map;
  }, [scores]);

  const existingRoundsForPlayer = useMemo(() => {
    if (!selectedPlayerId) return [];
    return playersWithScores[selectedPlayerId] || [];
  }, [selectedPlayerId, playersWithScores]);

  const rounds = useMemo(() => {
    const rs = [...new Set(scores.map(s => s.round))].sort((a, b) => a - b);
    return ['All', ...rs.map(String)];
  }, [scores]);

  const filtered = useMemo(() => scores.filter(s => {
    const name = `${s.player?.first_name || ''} ${s.player?.last_name || ''}`.toLowerCase();
    const matchSearch = name.includes(search.toLowerCase()) ||
      (s.player?.resident_club || '').toLowerCase().includes(search.toLowerCase());
    const matchRound = roundFilter === 'All' || s.round === parseInt(roundFilter);
    return matchSearch && matchRound;
  }), [scores, search, roundFilter]);

  return (
    <div className="pb-4 space-y-4">
      {/* Stats */}
      <div className="px-4 pt-4 grid grid-cols-3 gap-2">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 text-center">
          <p className="text-xl font-bold text-golf-gray">{scores.length}</p>
          <p className="text-xs text-gray-500">Entries</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 text-center">
          <p className="text-xl font-bold text-golf-gray">{rounds.length - 1}</p>
          <p className="text-xs text-gray-500">Rounds</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 text-center">
          <p className="text-xl font-bold text-golf-gray">
            {scores.filter(s => s.player_status === 'PLAYED').length || 0}
          </p>
          <p className="text-xs text-gray-500">Played</p>
        </div>
      </div>

      {/* Sticky Search + Round Filters */}
      <div className="sticky top-14 z-10 bg-gray-50 px-4 pt-2 pb-3 space-y-2 border-b border-gray-100">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search players..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-golf-orange bg-white"
            />
          </div>
          <button
            onClick={() => setIsPlayerSelectOpen(true)}
            className="bg-golf-orange text-white rounded-xl px-3 py-2.5 flex items-center gap-1.5 text-sm font-medium flex-shrink-0"
          >
            <PlusIcon className="w-4 h-4" />
            Add
          </button>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-0.5 scrollbar-hide">
          {rounds.map(r => (
            <button
              key={r}
              onClick={() => setRoundFilter(r)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                roundFilter === r ? 'bg-golf-orange text-white border-golf-orange' : 'bg-white text-gray-600 border-gray-200'
              }`}
            >
              {r === 'All' ? 'All Rounds' : `Round ${r}`}
            </button>
          ))}
        </div>
      </div>

      {/* Score list */}
      {loading ? (
        <div className="px-4 flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="px-4 text-center py-16">
          <TrophyIcon className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">No scores recorded yet</p>
          <button
            onClick={() => setIsPlayerSelectOpen(true)}
            className="mt-4 px-4 py-2 bg-golf-orange text-white text-sm font-medium rounded-xl"
          >
            Add First Score
          </button>
        </div>
      ) : (
        <div className="px-4 space-y-2">
          {filtered.map((score, idx) => (
            <div
              key={score.id}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-golf-orange/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-golf-orange">{idx + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-golf-gray text-sm truncate">
                    {score.player?.first_name} {score.player?.last_name}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{score.player?.resident_club}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-bold text-golf-gray">{score.score ?? '—'}</p>
                  <p className="text-xs text-gray-400">Score</p>
                </div>
                <div className="text-right flex-shrink-0 ml-1">
                  <p className="text-xs text-gray-400">R{score.round}</p>
                  <span className={`text-xs font-medium ${score.player_status === 'PLAYED' ? 'text-green-600' : score.player_status === 'DNP' ? 'text-red-500' : 'text-gray-400'}`}>
                    {score.player_status || '—'}
                  </span>
                </div>
                <button
                  onClick={() => setEditingScore(score)}
                  className="p-2 rounded-xl text-gray-400 hover:text-golf-orange hover:bg-golf-orange/5 transition-colors flex-shrink-0"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <MobilePlayerSelectionSheet
        isOpen={isPlayerSelectOpen}
        onClose={() => setIsPlayerSelectOpen(false)}
        players={players}
        playersWithScores={playersWithScores}
        onSelectPlayer={id => {
          setSelectedPlayerId(id);
          setEditingScore(null);
        }}
      />

      {selectedPlayerId && tournamentId && (
        <MobilePlayerScoreSheet
          isOpen={!!selectedPlayerId}
          onClose={() => { setSelectedPlayerId(null); fetchScores(); }}
          tournamentId={tournamentId}
          score={null}
          playerId={selectedPlayerId}
          onSuccess={fetchScores}
          existingRounds={existingRoundsForPlayer}
        />
      )}

      {editingScore && tournamentId && (
        <MobilePlayerScoreSheet
          isOpen={!!editingScore}
          onClose={() => { setEditingScore(null); fetchScores(); }}
          tournamentId={tournamentId}
          score={editingScore}
          playerId={editingScore.player_id}
          onSuccess={fetchScores}
          existingRounds={playersWithScores[editingScore.player_id] || []}
        />
      )}
    </div>
  );
}
